import { useCallback, useEffect, useRef, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { listPermissions } from "./permissions.gateway";
import type { Permission, PermissionsQueryState } from "./permissions.types";

export const SEARCH_DEBOUNCE_MS = 300;

export const PERMISSIONS_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar as permissões do Hub.";
export const PERMISSIONS_ERROR_MESSAGE = "Não foi possível carregar as permissões.";

const INITIAL_STATE: PermissionsQueryState = {
  status: "loading",
  permissions: [],
  total: 0,
  errorMessage: null,
};

export interface UsePermissionsParams {
  search: string;
  page: number;
  pageSize: number;
}

interface UsePermissionsResult extends PermissionsQueryState {
  reload: () => void;
  /** Reflete uma permissão já atualizada na listagem em memória, sem nova consulta. */
  applyPermission: (permission: Permission) => void;
}

export function usePermissions({
  search,
  page,
  pageSize,
}: UsePermissionsParams): UsePermissionsResult {
  const [state, setState] = useState<PermissionsQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);

  const term = search.trim();

  useEffect(() => {
    // Debounce só na digitação; carga inicial, paginação e retry são imediatos.
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        listPermissions({
          pesquisa: term,
          pagina: page,
          tamanhoPagina: pageSize,
          signal: controller.signal,
        })
          .then(({ permissions, total }) => {
            if (cancelled) return;
            setState({ status: "success", permissions, total, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;

            const isForbidden = isAPIError(error) && error.status === 403;
            setState({
              status: isForbidden ? "forbidden" : "error",
              permissions: [],
              total: 0,
              errorMessage: isForbidden
                ? PERMISSIONS_FORBIDDEN_MESSAGE
                : getErrorMessage(error) || PERMISSIONS_ERROR_MESSAGE,
            });
          });
      },
      shouldDebounce ? SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, page, pageSize, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const applyPermission = useCallback((permission: Permission) => {
    setState((previous) => ({
      ...previous,
      permissions: previous.permissions.map((item) =>
        item.id === permission.id ? permission : item,
      ),
    }));
  }, []);

  return { ...state, reload, applyPermission };
}
